// YTVideoPickerModal — Feb 2026 (Randy pt-yt-video-picker). Lets the creator
// pick which uploaded videos the AI YouTube Comment Replies module should
// monitor. Randy's spec:
//   - Title + search filter
//   - Select all / Deselect all buttons
//   - "Match current strikethrough" button that reads the YT Digital Twin
//     library and pre-checks any video that's currently struck-through.
//     If the strikethrough library was deleted entirely, this button is
//     inert (nothing to read against) but everything else still works.
import { useEffect, useMemo, useState } from "react";
import { X, MagnifyingGlass, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ytApi } from "@/lib/ytApi";

export default function YTVideoPickerModal({ siloId, channelSlot, initialSelected = [], onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [struckIds, setStruckIds] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  // Jun 2026 (Randy) — PROCESSED comes from the durable ledger, not from
  // strikethrough. Strikes live in a deletable library; the ledger is what
  // actually answers "is this video's content in my silo?"
  const [processedIds, setProcessedIds] = useState([]);
  const [gaps, setGaps] = useState(null);
  const [videosUnavailable, setVideosUnavailable] = useState(false);
  const [surfaceLabel, setSurfaceLabel] = useState("");
  const [hasStrikeLib, setHasStrikeLib] = useState(false);
  const [selected, setSelected] = useState(new Set(initialSelected));
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      ytApi.listChannelVideos(siloId, channelSlot).then((r) => {
        if (cancelled) return;
        setVideos(r.videos || []);
        setStruckIds(r.struck_video_ids || []);
        setDeletedIds(r.deleted_video_ids || []);
        setProcessedIds(r.processed_video_ids || []);
        setGaps(r.gaps || null);
        // Feb 2026 (Randy pt-yt-picker-surface-scope · iter164) — surface
        // hint tells the user which library this picker is scoped to,
        // so the STRUCK/DELETED counts they see make intuitive sense.
        setSurfaceLabel(r.surface === "file_refinery" ? "Refinery" : "Workbench");
        setHasStrikeLib(!!r.has_strikethrough_library);
        // Feb 17, 2026 (Randy pt-strike-live-sync) — also refresh the
        // monitored (`selected`) set every load so any additions/removals
        // the backend made after strike-flips reflect instantly. Only
        // refresh when the user hasn't started making unsaved edits.
        try {
          const fresh = new Set(r.monitored_video_ids || []);
          setSelected((prev) => {
            // If the user has already toggled locally, don't stomp their
            // work — just keep whatever they have. Detect by comparing
            // against the ORIGINAL initialSelected we mounted with.
            const initial = new Set(initialSelected);
            const sameAsInitial = prev.size === initial.size &&
              [...prev].every((v) => initial.has(v));
            return sameAsInitial ? fresh : prev;
          });
        } catch { /* noop */ }
      }).catch((e) => {
        if (cancelled) return;
        toast.error(e.response?.data?.detail || "Couldn't load channel videos.");
        // Jun 2026 (Randy) — the uploads list needs a live YouTube call, which
        // dies on an expired token or an exhausted daily quota. The coverage
        // holes don't: /gaps reads the ledger + integration only. So when the
        // list fails, still tell the creator what's wrong with their coverage
        // instead of leaving them with an empty modal.
        ytApi.coverageGaps(siloId, channelSlot).then((g) => {
          if (cancelled) return;
          setGaps(g?.gaps || null);
          setProcessedIds(g?.processed_video_ids || []);
          setVideosUnavailable(true);
        }).catch(() => { /* nothing more we can say */ });
      }).finally(() => { if (!cancelled) setLoading(false); });
    };
    load();
    // Feb 17, 2026 (Randy pt-strike-live-sync) — listen for strike-flip
    // events from the YouTube Tool library table so this picker refreshes
    // live (no page reload). YouTubeTool.updateRow dispatches the event
    // right after the PATCH succeeds; the backend has already synced
    // `youtube_integrations.monitored_video_ids` server-side.
    const onStrike = (ev) => {
      if (!ev?.detail) return;
      if (ev.detail.silo_id && ev.detail.silo_id !== siloId) return;
      load();
    };
    window.addEventListener("mysilo:yt-strike-changed", onStrike);
    return () => {
      cancelled = true;
      window.removeEventListener("mysilo:yt-strike-changed", onStrike);
    };
  }, [siloId, channelSlot, initialSelected]);

  const filtered = useMemo(() => {
    if (!q.trim()) return videos;
    const query = q.toLowerCase();
    return videos.filter((v) => (v.title || "").toLowerCase().includes(query) || (v.video_id || "").toLowerCase().includes(query));
  }, [videos, q]);

  const toggle = (vid) => {
    const next = new Set(selected);
    if (next.has(vid)) next.delete(vid); else next.add(vid);
    setSelected(next);
  };
  const selectAllFiltered = () => {
    const next = new Set(selected);
    filtered.forEach((v) => next.add(v.video_id));
    setSelected(next);
  };
  const deselectAllFiltered = () => {
    const next = new Set(selected);
    filtered.forEach((v) => next.delete(v.video_id));
    setSelected(next);
  };
  const matchStrikethrough = () => {
    if (!hasStrikeLib) return toast.info("No strikethrough library exists yet — nothing to match against.");
    const next = new Set(selected);
    struckIds.forEach((id) => next.add(id));
    setSelected(next);
    toast.success(`Matched ${struckIds.length} struck video${struckIds.length === 1 ? "" : "s"}.`);
  };
  // Jun 2026 (Randy) — the honest version of the button above: check exactly
  // the videos whose content is actually in the silo, read from the processed
  // ledger instead of strikethrough (which a library delete can wipe).
  const matchProcessed = () => {
    if (!processedIds.length) return toast.info("Nothing processed into this silo yet.");
    const next = new Set(selected);
    processedIds.forEach((id) => next.add(id));
    setSelected(next);
    toast.success(`Matched ${processedIds.length} processed video${processedIds.length === 1 ? "" : "s"}.`);
  };
  const save = async () => {
    setSaving(true);
    try {
      await onSaved(Array.from(selected));
      toast.success(`Saved — ${selected.size} video${selected.size === 1 ? "" : "s"} monitored.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[220] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="yt-video-picker-modal" onClick={onClose}>
      <div className="w-full max-w-xl bg-[#FDFCF8] border hairline rounded-xl shadow-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-4 py-3 border-b hairline">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] font-medium">Video monitor</div>
            <div className="text-[14px] font-medium text-[#1C1B1A]">Pick videos the AI watches for questions</div>
            <div className="text-[11px] text-[#6B6A68] mt-0.5">
              {selected.size} of {videos.length} selected
              {surfaceLabel && <span className="ml-1 text-[#6B6A68]" data-testid="yt-video-picker-surface-hint">· {surfaceLabel} library</span>}
              {videos.length >= 1000 && <span className="ml-1 text-[#B8862F] italic">(showing first 1000)</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="yt-video-picker-close"><X size={16} /></button>
        </div>

        <div className="px-4 py-2 border-b hairline bg-white/40 space-y-2">
          <div className="relative">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-[#6B6A68]" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search video titles…"
              data-testid="yt-video-picker-search"
              className="w-full bg-white border hairline rounded-md pl-7 pr-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <button onClick={selectAllFiltered} data-testid="yt-video-picker-select-all"
              className="text-[10.5px] px-2 py-1 rounded border hairline text-[#4A5D4E] hover:bg-[#F0F1EE]">Select all{q ? " (filtered)" : ""}</button>
            <button onClick={deselectAllFiltered} data-testid="yt-video-picker-deselect-all"
              className="text-[10.5px] px-2 py-1 rounded border hairline text-[#4A5D4E] hover:bg-[#F0F1EE]">Deselect all{q ? " (filtered)" : ""}</button>
            <button onClick={matchStrikethrough} disabled={!hasStrikeLib}
              data-testid="yt-video-picker-match-strikethrough"
              title={hasStrikeLib ? "Auto-check every video that's currently struck through in your YT Digital Twin library" : "No YT Digital Twin library found for this channel"}
              className="text-[10.5px] px-2 py-1 rounded border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-40 disabled:cursor-not-allowed">
              Match current strikethrough{hasStrikeLib ? ` (${struckIds.length})` : ""}
            </button>
            <button onClick={matchProcessed} disabled={!processedIds.length}
              data-testid="yt-video-picker-match-processed"
              title="Check every video whose content is actually in this silo. Survives a library delete — read from the processed ledger, not strikethrough."
              className="text-[10.5px] px-2 py-1 rounded border border-[#B9D4BE] bg-[#EAF3EC]/60 text-[#2F5133] hover:bg-[#EAF3EC] disabled:opacity-40 disabled:cursor-not-allowed">
              Match processed{processedIds.length ? ` (${processedIds.length})` : ""}
            </button>
          </div>
          {gaps?.total > 0 && (
            <div className="bg-[#FDF8EE] border border-[#E8D9B0] rounded-md px-2.5 py-2 text-[11px] text-[#6B5A33] space-y-1" data-testid="yt-video-picker-gaps">
              {gaps.monitored_unprocessed_count > 0 && (
                <div data-testid="yt-video-picker-gap-unprocessed">
                  <strong>{gaps.monitored_unprocessed_count}</strong> checked video{gaps.monitored_unprocessed_count === 1 ? "" : "s"} {gaps.monitored_unprocessed_count === 1 ? "has" : "have"} never
                  been processed — replies there get built from the rest of your silo and come out generic.
                </div>
              )}
              {gaps.excluded_processed_count > 0 && (
                <div data-testid="yt-video-picker-gap-excluded">
                  <strong>{gaps.excluded_processed_count}</strong> processed video{gaps.excluded_processed_count === 1 ? " is" : "s are"} left
                  unchecked, so {gaps.excluded_processed_count === 1 ? "its" : "their"} comments stay unanswered.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-3 overflow-y-auto flex-1">
          {loading && <div className="text-center py-8 text-[12px] text-[#6B6A68]">Loading your channel videos…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-8 text-[12px] text-[#6B6A68]" data-testid="yt-video-picker-empty">
              {q ? "No videos match that search."
                 : videosUnavailable
                   ? "Couldn't reach YouTube for your upload list — your saved picks and the coverage notice above are unaffected."
                   : "No videos found on this channel."}
            </div>
          )}
          {!loading && filtered.map((v) => {
            const isChecked = selected.has(v.video_id);
            const isDeleted = deletedIds.includes(v.video_id);
            const isProcessed = !isDeleted && processedIds.includes(v.video_id);
            const isStruck = !isDeleted && !isProcessed && struckIds.includes(v.video_id);
            // Feb 2026 (Randy pt-yt-picker-surface-scope · iter164) —
            // DELETED beats STRUCK. A video removed from Randy's library
            // gets a distinct red "DELETED" pill so it stops being
            // silently included in the "Match current strikethrough"
            // mental model. Row stays selectable (Randy: "I don't care
            // if it pulls them all — just don't mark them as struck").
            return (
              <label key={v.video_id} data-testid={`yt-video-picker-row-${v.video_id}`}
                className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer ${isChecked ? "bg-[#EAF3EC]" : "hover:bg-[#F0F1EE]/60"}`}>
                <input type="checkbox" checked={isChecked} onChange={() => toggle(v.video_id)} className="mt-1" />
                <div className="min-w-0 flex-1">
                  <div className={`text-[12px] leading-snug ${isDeleted ? "line-through text-[#B54848]" : (isStruck || isProcessed) ? "line-through text-[#6B6A68]" : "text-[#1C1B1A]"}`}>{v.title || v.video_id}</div>
                  <div className="text-[10px] text-[#6B6A68] font-mono">
                    {v.video_id}
                    {isDeleted && <span className="ml-2 uppercase tracking-[0.14em] text-[9px] text-[#B54848] font-sans font-medium" data-testid={`yt-video-picker-deleted-tag-${v.video_id}`}>deleted</span>}
                    {isProcessed && <span className="ml-2 uppercase tracking-[0.14em] text-[9px] text-[#2F6B3A] font-sans font-medium" data-testid={`yt-video-picker-processed-tag-${v.video_id}`}>in your silo</span>}
                    {isStruck && <span className="ml-2 uppercase tracking-[0.14em] text-[9px] text-[#C16E5A] font-sans">struck</span>}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 border-t hairline px-4 py-3 bg-white/40">
          {/* Feb 2026 (Randy pt-yt-picker-surface-scope · iter164) — subtext
              flags any selections that are currently marked DELETED in
              this channel_slot's library, so Randy sees the honest state
              of his saved list at save-time. */}
          {(() => {
            const deletedInSelection = Array.from(selected).filter((id) => deletedIds.includes(id)).length;
            if (deletedInSelection === 0) return null;
            return (
              <span className="text-[10.5px] text-[#B54848] italic mr-auto" data-testid="yt-video-picker-deleted-in-selection">
                {deletedInSelection} of your {selected.size} selection{selected.size === 1 ? "" : "s"} marked DELETED in this library
              </span>
            );
          })()}
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]" data-testid="yt-video-picker-cancel">Cancel</button>
          <button onClick={save} disabled={saving}
            data-testid="yt-video-picker-save"
            className="text-[12px] px-4 py-1.5 rounded-md bg-[#2F6B3A] text-white hover:bg-[#245029] disabled:opacity-40 flex items-center gap-1.5">
            <Check size={11} weight="bold" /> {saving ? "Saving…" : `Save (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}
